import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, SignedIn, SignedOut, SignIn, SignInButton, SignUpButton, UserButton, useAuth, useUser } from '@clerk/clerk-react';
import type { AnalyticsEvent } from '../core/events';
import { ApiError, call, type GetToken, type Me, type OwnerProfile, type VisitorProfile } from './api';
import { createAnalytics, nullAnalytics, type Analytics } from './analytics/client';
import { chosenE2eIdentity, clerkConfiguration, selectE2eIdentity } from './auth-config';
import { useClerkAccount } from './clerk-account';
import { LabsNav } from '@240tutoring/labs-ui/labs-nav';
import { ProfilePage } from './components/ProfilePage';
import { SignInPrompt } from './components/SignInPrompt';
import { StaffOnly } from './components/StaffOnly';
import { Unavailable } from './components/Unavailable';
import { HOME_HREF, linkTo, parseRoute, type Route } from './navigation';

/** The bar every Labs tool shares, and the page under it. */
function Shell({ headerAction, children }: { headerAction?: ReactNode; children: ReactNode }) {
  return <>
    <LabsNav name="240 Profile" hideLabsChip account={headerAction} />
    <main className="app-shell">{children}</main>
  </>;
}

/** The current route, following Back, Forward, and in-app navigation. */
function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const update = () => { setRoute(parseRoute(window.location.pathname)); window.scrollTo(0, 0); };
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return route;
}

/**
 * The kill switch's own 503 (`{ error: 'unavailable' }` from the owner gate and the visitor
 * API). Any other 503, such as the Worker's safety net, is the Worker not answering.
 */
const switchedOff = (e: unknown) => e instanceof ApiError && e.status === 503 && e.body.error === 'unavailable';

const deniedReason = (failure: unknown) => {
  const e = failure as ApiError;
  if (e.reason === 'not_staff') return e.reason;
  if (switchedOff(e)) return 'disabled';
  return e.status === 401 ? 'session_invalid' : 'unreachable';
};

type OwnerStage = { kind: 'loading' } | { kind: 'ready'; me: Me; profile: OwnerProfile } | { kind: 'denied'; reason: string };

/**
 * Profile's home: the owner's own page, created hidden on the first visit (F1). The owner gate
 * decides who gets here; a non-staff account sees `StaffOnly` and no way to make one (R18).
 */
/** The 240 account the name and photo come from (`useClerkAccount`); absent without Clerk. */
type Account = { open(): void; version: number };

function OwnerHome({ getToken, onTrack, account }: { getToken: GetToken; onTrack(event: AnalyticsEvent): void; account?: Account }) {
  const [stage, setStage] = useState<OwnerStage>({ kind: 'loading' });
  const [boot, setBoot] = useState(0);
  useEffect(() => {
    let live = true;
    setStage({ kind: 'loading' });
    void (async () => {
      try {
        // Independent: both pass the same owner gate, and the profile route creates the row itself.
        const [me, profile] = await Promise.all([call<Me>(getToken, '/api/me'), call<OwnerProfile>(getToken, '/api/me/profile')]);
        if (live) setStage({ kind: 'ready', me, profile });
      } catch (failure) {
        if (live) setStage({ kind: 'denied', reason: deniedReason(failure) });
      }
    })();
    return () => { live = false; };
  }, [getToken, boot]);
  if (stage.kind === 'loading') return <p className="notice" role="status">Loading your profile…</p>;
  if (stage.kind === 'denied') return stage.reason === 'not_staff' ? <StaffOnly /> : <Unavailable reason={stage.reason} onRetry={() => setBoot((n) => n + 1)} />;
  return <>
    {stage.me.fixtures && <div className="fixture-banner" role="status"><strong>Sample lookup mode.</strong> Verify answers from captured state pages, not the live sites.</div>}
    <ProfilePage mode="owner" initial={stage.profile} getToken={getToken} verify={stage.me.verify} onTrack={onTrack} account={account} staff={stage.me.staff} />
  </>;
}

type VisitorStage = { kind: 'loading' } | { kind: 'ready'; profile: VisitorProfile } | { kind: 'sign_in' } | { kind: 'not_found' } | { kind: 'failed'; reason: string };

/**
 * A profile link (F3, R10). Each refusal is the Worker's: 401 asks for sign-in, 404 is the one
 * answer for hidden and missing alike, 503 is Profile switched off. The owner opening their own
 * link sees the visitor's page and a way back to editing (R11).
 */
function VisitorHome({ slug, getToken, signIn }: { slug: string; getToken: GetToken; signIn?: ReactNode }) {
  const [stage, setStage] = useState<VisitorStage>({ kind: 'loading' });
  const [boot, setBoot] = useState(0);
  useEffect(() => {
    let live = true;
    setStage({ kind: 'loading' });
    void (async () => {
      try {
        const profile = await call<VisitorProfile>(getToken, `/api/p/${encodeURIComponent(slug)}`);
        if (live) setStage({ kind: 'ready', profile });
      } catch (failure) {
        if (!live) return;
        const e = failure as ApiError;
        setStage(e.status === 401 ? { kind: 'sign_in' } : e.status === 404 ? { kind: 'not_found' } : { kind: 'failed', reason: switchedOff(e) ? 'disabled' : 'unreachable' });
      }
    })();
    return () => { live = false; };
  }, [getToken, slug, boot]);
  switch (stage.kind) {
    case 'loading': return <p className="notice" role="status">Opening this profile…</p>;
    case 'sign_in': return <SignInPrompt action={signIn} />;
    case 'not_found': return <section className="notice"><h2>This page isn’t available</h2><p>The link may be mistyped, or its owner may have stopped sharing it.</p></section>;
    case 'failed': return <Unavailable reason={stage.reason} onRetry={() => setBoot((n) => n + 1)} />;
    case 'ready': return <>
      {stage.profile.owner && <div className="owner-preview" role="note">
        <p>This is your page as visitors see it.</p>
        <a className="secondary" {...linkTo(HOME_HREF)}>Edit your profile</a>
      </div>}
      <ProfilePage mode="visitor" profile={stage.profile} getToken={getToken} />
    </>;
  }
}

/** A signed-in viewer: the owner's home, or a profile link. */
export function ProfileApp({ auth, headerAction, analytics: injected, signIn, account }: {
  auth: { userId: string; email?: string | null; getToken: GetToken };
  headerAction?: ReactNode;
  analytics?: Analytics;
  signIn?: ReactNode;
  account?: Account;
}) {
  const tokenRef = useRef(auth.getToken); tokenRef.current = auth.getToken;
  const getToken = useCallback(() => tokenRef.current(), []);
  const analyticsRef = useRef<Analytics>(injected ?? nullAnalytics());
  useEffect(() => {
    // Identified by email, as Planner does, so staff testing it can be told apart (R15).
    const adapter = injected ?? createAnalytics(auth.userId, auth.email ?? null);
    analyticsRef.current = adapter;
    return () => { if (!injected) adapter.dispose(); analyticsRef.current = nullAnalytics(); };
  }, [auth.userId, auth.email, injected]);
  const track = useCallback((event: AnalyticsEvent) => { try { analyticsRef.current.track(event); } catch { /* Analytics is optional. */ } }, []);
  const route = useRoute();
  return <Shell headerAction={headerAction}>
    {route.name === 'profile'
      ? <VisitorHome key={route.slug} slug={route.slug} getToken={getToken} signIn={signIn} />
      : <OwnerHome getToken={getToken} onTrack={track} account={account} />}
  </Shell>;
}

const signedOut = async () => null;

/** A signed-out viewer of a profile link: public pages open, the rest ask for sign-in. */
function SignedOutLink({ slug, signIn }: { slug: string; signIn?: ReactNode }) {
  return <Shell><VisitorHome slug={slug} getToken={signedOut} signIn={signIn} /></Shell>;
}

function SignedOutHome({ signIn }: { signIn?: ReactNode }) {
  return <main className="auth-page"><div className="eyebrow">240 LABS</div><h1>240 Profile</h1><p>Sign in to build the page you hand another teacher.</p>{signIn && <div className="sign-in-controls">{signIn}</div>}</main>;
}

function ClerkAuthed({ signIn }: { signIn: ReactNode }) {
  const { getToken, userId, sessionId } = useAuth();
  const { isLoaded, user } = useUser();
  const account = useClerkAccount();
  if (!isLoaded || !user || !userId) return <main className="auth-page" role="status">Checking your sign-in…</main>;
  return <ProfileApp key={`${userId}:${sessionId}`} auth={{ getToken, userId, email: user.primaryEmailAddress?.emailAddress ?? null }} headerAction={<UserButton />} signIn={signIn} account={account} />;
}

export default function App() {
  const route = useRoute();
  const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!key && import.meta.env.VITE_E2E_IDENTITY) {
    const testIdentity = selectE2eIdentity(import.meta.env.VITE_E2E_IDENTITY, chosenE2eIdentity());
    const banner = <div className="test-banner">Test identity build · Sign-in is bypassed. Not for production.</div>;
    if (!testIdentity) return <>{banner}{route.name === 'profile' ? <SignedOutLink key={route.slug} slug={route.slug} /> : <SignedOutHome />}</>;
    return <>{banner}<ProfileApp analytics={nullAnalytics()} auth={{ userId: testIdentity.split(':')[0]!, email: testIdentity.split(':')[1] ?? null, getToken: async () => testIdentity }} /></>;
  }
  if (!key) return <main className="auth-page"><div className="eyebrow">240 LABS</div><h1>240 Profile</h1><p>Sign-in is not configured for this build.</p><p>Set the Clerk publishable key and rebuild to continue.</p></main>;
  const config = clerkConfiguration(window.location.origin, window.location.pathname);
  const signIn = config.hosted
    ? <><SignInButton mode="redirect"><button className="primary">Sign in with your 240 account</button></SignInButton><SignUpButton mode="redirect"><button className="text-button">Create an account</button></SignUpButton></>
    : <SignIn routing="hash" />;
  return <ClerkProvider publishableKey={key} {...config.provider}>
    <SignedOut>
      {route.name === 'profile'
        ? <SignedOutLink key={route.slug} slug={route.slug} signIn={signIn} />
        : <SignedOutHome signIn={signIn} />}
    </SignedOut>
    <SignedIn><ClerkAuthed signIn={signIn} /></SignedIn>
  </ClerkProvider>;
}
